import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { validateImage, validateImageDimensions, resizeImage } from '../utils/imageUtils';
import axios from 'axios';

interface ImageUploadProps {
  value?: File | string | null;
  onChange: (file: File | null) => void;
  error?: string;
  disabled?: boolean;
  locationId?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  locationId,
}) => {
  const { t } = useTranslation();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate preview URL when value changes
  React.useEffect(() => {
    let url: string | null = null;
    if (value instanceof File) {
      url = URL.createObjectURL(value);
      setPreviewUrl(url);
    } else if (typeof value === 'string' && value) {
      setPreviewUrl(value);
    } else {
      setPreviewUrl(null);
    }
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [value]);

  // Handle image load errors
  const handleImageError = () => {
    setPreviewUrl(null);
    onChange(null); // Clear the value when image fails to load
  };



  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleRemoveImage = () => {
    // If it's a new upload, just clear
    if (value instanceof File || !locationId || typeof value !== 'string') {
      onChange(null);
      setValidationError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    // Otherwise, confirm deletion for existing image
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!locationId) return;
    setIsDeleting(true);
    setValidationError(null);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/locations/${locationId}/image`);
      onChange(null);
      setConfirmOpen(false);
    } catch (err) {
      setValidationError(t('imageUpload.failedToDelete'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      await processFile(imageFile);
    } else {
      setValidationError(t('imageUpload.dropValidImage'));
    }
  };

  const processFile = async (file: File) => {
    setIsValidating(true);
    setValidationError(null);

    try {
      // Validate file type and size
      const basicValidation = validateImage(file);
      if (!basicValidation.isValid) {
        setValidationError(basicValidation.error!);
        return;
      }

      // Validate dimensions
      const dimensionValidation = await validateImageDimensions(file);
      if (!dimensionValidation.isValid) {
        setValidationError(dimensionValidation.error!);
        return;
      }

      // Resize image if needed
      const processedFile = await resizeImage(file);
      
      onChange(processedFile);
    } catch (err) {
      setValidationError(t('imageUpload.failedToProcess'));
    } finally {
      setIsValidating(false);
    }
  };

  const getDisplayError = () => {
    return validationError || error;
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
        {t('imageUpload.providerLogo')}
      </Typography>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {previewUrl ? (
        <Paper
          sx={{
            p: 2,
            width: 250,
            height: 250,
            margin: '0 auto',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
            <Box
              component="img"
              src={previewUrl}
              alt="Preview"
              onError={handleImageError}
              sx={{
                width: 250,
                height: 250,
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            {/* File info and delete button can be stacked below image if needed */}
          </Box>
          <IconButton
            onClick={handleRemoveImage}
            disabled={disabled}
            color="error"
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <DeleteIcon />
          </IconButton>
        </Paper>
      ) : (
        <Paper
          sx={{
            p: 3,
            width: 250,
            height: 250,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px dashed',
            borderColor: isDragOver 
              ? 'primary.main' 
              : getDisplayError() 
                ? 'error.main' 
                : 'divider',
            borderRadius: 2,
            textAlign: 'center',
            cursor: disabled ? 'default' : 'pointer',
            backgroundColor: isDragOver ? 'action.hover' : 'transparent',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: disabled ? 'divider' : 'primary.main',
              backgroundColor: disabled ? 'transparent' : 'action.hover',
            },
          }}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isValidating ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('imageUpload.uploadImage')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('imageUpload.clickToSelectOrDrag')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {t('imageUpload.fileRequirements')}
              </Typography>
            </>
          )}
        </Paper>
      )}

      {getDisplayError() && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {getDisplayError()}
        </Alert>
      )}

      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{t('imageUpload.confirmDeletion')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('imageUpload.confirmDeleteMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : t('imageUpload.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageUpload; 